import { useState } from 'react';
import { toast } from 'sonner';

import { downloadCsv } from '@/marketing/lib/csvExport';
import { contentTrackingAPIClient } from '@/marketing/infrastructure/influencer/campaign/contentTracking/contentTrackingAPIClient';
import {
  CONTENT_TRACKING_PAGE_LIMIT,
  type ContentTrackingExportPurpose,
  type ContentTrackingExportRow,
  type ContentTrackingListParams,
} from '@/marketing/models/contentTracking';
import { generateContentTrackingCsv } from './contentTrackingCsv';

// Walks every export page for the current filters before writing one CSV, so the file is never a partial page.
export function useContentTrackingExport(campaignId: string, params: ContentTrackingListParams) {
  const [exporting, setExporting] = useState<ContentTrackingExportPurpose | null>(null);

  const exportCsv = async (purpose: ContentTrackingExportPurpose) => {
    if (exporting) {
      return;
    }
    setExporting(purpose);
    try {
      const rows: ContentTrackingExportRow[] = [];
      let cursor: string | undefined;
      do {
        const page = await contentTrackingAPIClient.exportPage(campaignId, purpose, {
          ...params,
          limit: CONTENT_TRACKING_PAGE_LIMIT,
          ...(cursor ? { cursor } : {}),
        });
        rows.push(...page.rows);
        cursor = page.pageInfo.nextCursor ?? undefined;
      } while (cursor);
      const date = new Date().toISOString().slice(0, 10);
      downloadCsv(generateContentTrackingCsv(rows, purpose), `content-tracking-${purpose}-${date}.csv`);
      toast.success(`Exported ${rows.length} creators`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  return { exporting, exportCsv };
}
